import { type FC, useEffect, useState } from 'react';
import { createProvider } from '../../../utils/createProvider';
import { useToolNavigationActiveModuleProvider } from './ToolNavigationActiveModuleProvider';
import { PrimaryNavigationItem } from '../types';
import { useToolNavigationDataProvider } from './ToolNavigationDataProvider';

type ContextValue = {
	isAdmin: boolean;
	switchToUser: () => void;
	switchToAdmin: () => void;
	hasUserNavigation: boolean;
	hasAdminNavigation: boolean;
};

const [Provider, useProvider] = createProvider<ContextValue>({
	contextName: 'ToolNavigationRoleSwitcherContext',
});

export const useToolNavigationRoleSwitcherProvider = useProvider;

const itemsPlaceholder = []; // keeps the same reference between renders

export const ToolNavigationRoleSwitcherProvider: FC<React.PropsWithChildren> = ({ children }) => {
	const navData = useToolNavigationDataProvider();
	const adminNavigationItems = navData?.adminNavigationItems || itemsPlaceholder;
	const userNavigationItems = navData?.userNavigationItems || itemsPlaceholder;
	const { listPageTypeId } = useToolNavigationActiveModuleProvider();

	const [isAdmin, setIsAdmin] = useState(getIsAdmin(adminNavigationItems, listPageTypeId));

	useEffect(() => {
		const nextIsAdmin = getIsAdmin(adminNavigationItems, listPageTypeId);

		setIsAdmin(nextIsAdmin);
	}, [adminNavigationItems, listPageTypeId]);

	const hasUserNavigation = userNavigationItems.length !== 0;
	const hasAdminNavigation = adminNavigationItems.length !== 0;

	return (
		<Provider
			value={{
				isAdmin,
				switchToUser: () => setIsAdmin(false),
				switchToAdmin: () => setIsAdmin(true),
				hasUserNavigation,
				hasAdminNavigation,
			}}
		>
			{children}
		</Provider>
	);
};

function getIsAdmin(adminNavItems: PrimaryNavigationItem[], activePageTypeId: number | null) {
	return adminNavItems.some(
		(navItem) =>
			navItem.pageTypeId === activePageTypeId ||
			navItem.childNavigationItems.some((i) => i.pageTypeId === activePageTypeId),
	);
}
